/**
 * =============================================================================
 * SHARED IMAGE-UPLOAD ORCHESTRATION (question + reply paths)
 * =============================================================================
 *
 * The never-throw, fail-open boundary for uploading one already-selected image,
 * plus the shared partial-failure vocabulary. Extracted from QuestionForm so the
 * question path (ADR-022 §12 PR5a) and the reply path (PR5-remainder) share ONE
 * tested implementation — no forked uploader, and no duplicated partial-failure
 * copy against the widget's tight gzip budget (BL-WIDGET-BUNDLE-BUDGET-001).
 *
 * INVARIANTS this module upholds:
 *   - Never throws (ADR-022 §8: image handling must never block submission). A
 *     `prepareImageForUpload` throw or an API failure both resolve to an `ok:false`
 *     result, so one bad image never aborts a submission or discards the created
 *     entity id (BL-WIDGET-UPLOAD-FAILURE-UX-001).
 *   - Client compression is best-effort UX only; the Worker's validate/strip/
 *     moderate chokepoint remains the authoritative gate (never a security control).
 *   - The upload-proof handle is opaque and is only ever passed through to the
 *     injected uploader (as the `X-Upload-Proof` header, wired in api/client.ts).
 *     It is never logged, persisted, or placed in a URL here (ADR-024 §10.5).
 *
 * =============================================================================
 */

import { prepareImageForUpload } from './prepareImageForUpload';
import { logger } from './logger';

/**
 * A selected image that failed to upload. Retains the actual `File` (not just
 * the name) so a retry can re-send only the failed files without re-selecting
 * (BL-WIDGET-UPLOAD-FAILURE-UX-001).
 */
export interface FailedImageUpload {
  file: File;
  name: string;
  message: string;
}

/** Outcome of a single image upload attempt. Never a thrown error. */
export type ImageUploadOutcome = { ok: true } | { ok: false; message: string };

/**
 * The minimal shape of a widget upload response this module needs. Both
 * `widgetApi.images.upload` and `widgetApi.images.uploadForReply` (whose full
 * type is `ApiResponse<UploadImageResult>`) are structurally assignable.
 */
type UploadResponse = { success: true } | { success: false; error: { message: string } };

/**
 * An id-anchored image uploader: `(targetId, file, uploadProof?) => response`.
 * `widgetApi.images.upload` (questionId) and `widgetApi.images.uploadForReply`
 * (replyId) both match this shape and are `this`-free (they delegate to the
 * module-level `apiUpload`), so they can be passed by reference.
 */
export type ImageUploadFn = (
  targetId: string,
  file: File,
  uploadProof?: string
) => Promise<UploadResponse>;

/**
 * Upload one image: best-effort client compression (PR5a, fail-open), then POST
 * via the injected uploader. Never throws — see the module INVARIANTS.
 *
 * @param upload     - the id-anchored uploader (question or reply variant)
 * @param targetId   - the questionId or replyId the image attaches to
 * @param file       - the user-selected image file
 * @param uploadProof - ADR-024 §11 opaque upload-proof handle (memory-only)
 */
export async function uploadOneImage(
  upload: ImageUploadFn,
  targetId: string,
  file: File,
  uploadProof?: string
): Promise<ImageUploadOutcome> {
  // PR5a: best-effort downscale/re-encode. Fail-open by its OWN contract
  // (returns the original on any internal failure), but guard it here too so a
  // hypothetical throw (a browser/decoder regression) uploads the ORIGINAL bytes
  // rather than counting the photo as failed — the invariant is "compression
  // never blocks the upload", and the server chokepoint remains the authoritative
  // size/privacy gate (UX/cost optimization only).
  let uploadFile = file;
  try {
    uploadFile = await prepareImageForUpload(file);
  } catch {
    uploadFile = file;
  }

  try {
    // ADR-024 §11: the uploader re-sends `uploadProof` as X-Upload-Proof so
    // ownership survives third-party-cookie blocking on ITP/ETP browsers.
    const response = await upload(targetId, uploadFile, uploadProof);
    if (response.success) return { ok: true };
    return { ok: false, message: response.error.message };
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : 'Upload failed' };
  }
}

/**
 * Upload a batch of images one at a time, collecting the failures. Never throws
 * (each attempt is a never-throw {@link uploadOneImage}); the returned list holds
 * the photos that did not attach, each retaining its `File` for a later retry.
 * Shared by the question retry path and both reply paths so the sequential
 * fail-open loop lives in one place.
 *
 * @param onProgress - optional, invoked with `(current, total)` (1-based) BEFORE
 *   each upload so a caller can render determinate progress ("Uploading photo
 *   X of N"). Never called when `files` is empty.
 * @returns the images that failed to upload (empty when all succeeded)
 */
export async function runImageUploadSequence(
  upload: ImageUploadFn,
  targetId: string,
  files: File[],
  uploadProof?: string,
  onProgress?: (current: number, total: number) => void
): Promise<FailedImageUpload[]> {
  const failed: FailedImageUpload[] = [];
  for (let i = 0; i < files.length; i++) {
    onProgress?.(i + 1, files.length);
    const file = files[i];
    const result = await uploadOneImage(upload, targetId, file, uploadProof);
    if (!result.ok) {
      failed.push({ file, name: file.name, message: result.message });
    }
  }
  return failed;
}

/**
 * Invoke a host callback (`onSuccess` **or** `onError`) defensively, shared by the
 * question and reply completion paths.
 *
 * A host callback is arbitrary embedding-page code (analytics, navigation); if it
 * throws we must never (a) let it bubble into an unhandled promise rejection out of
 * the fire-and-forget `onSubmit` handler, (b) flip our terminal screen (a throwing
 * `onSuccess` must not surface the error screen — which would read as a failed
 * submit and invite a duplicate), nor (c) let a throwing `onError` re-enter the
 * submit catch and fire `onError` a SECOND time with a different message. Its throw
 * is logged (dev only — stripped by drop_console in prod) and swallowed. Fail-open
 * parity with the never-throw image path (ADR-022 §8). For `onSuccess` the widget's
 * own terminal state is already committed (state set, upload-proof cleared) before
 * this runs; for `onError` the sanitized error state is likewise already set.
 *
 * The callback props are typed `(...) => void`, but TypeScript's void-returning-
 * callback rule accepts an `async` host handler (`() => Promise<void>`). A synchronous
 * try/catch alone would NOT contain that: `run()` would return a live promise the
 * catch never awaits, so a rejection would still float to the host page as an
 * `unhandledrejection` — the exact outcome (a) above. So we also attach a rejection
 * handler to any returned thenable.
 */
export function notifyHostCallback(run: () => void): void {
  try {
    // `run()` is `() => void` by type, but a host may pass an async callback (see
    // the docblock), so treat the return as possibly-thenable rather than trusting
    // the annotation.
    const result = run() as unknown;
    if (result && typeof (result as { then?: unknown }).then === 'function') {
      (result as Promise<unknown>).then(undefined, (error) =>
        logger.error('HOST_CALLBACK_THREW', error instanceof Error ? error : null)
      );
    }
  } catch (error) {
    logger.error(
      'HOST_CALLBACK_THREW',
      error instanceof Error ? error : null
    );
  }
}

/**
 * Human-readable summary for the partial-failure state, shared by the question
 * and reply forms. `subject` is the created entity ("question" | "reply").
 */
export function buildPartialUploadMessage(
  subject: 'question' | 'reply',
  failedCount: number,
  totalImages: number
): string {
  return `Your ${subject} was submitted and is pending review, but ${failedCount} of ${totalImages} photo${
    totalImages === 1 ? '' : 's'
  } could not be attached.`;
}
